import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, Settings2 } from "lucide-react";
import { api, formatApiError } from "../api";
import { BRANCHES } from "../constants/testIds";
import { fadeUp } from "../lib/motion";
import { Button } from "../components/ui/button";
import { StatusPill } from "../components/common/StatusPill";
import { GymLoader } from "../components/common/GymLoader";
import { ErrorState } from "../components/common/ErrorState";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { FormField, isFieldValid } from "../components/common/FormField";
import { SubmitButton } from "../components/common/SubmitButton";
import { Textarea } from "../components/ui/textarea";

// UX-GAP-01 §1.7: Owner Cockpit "Branches" — `POST /branches` (create), `PATCH /branches/{id}/
// profile` (DEFECT-D-08: name/address/contact) and `PATCH /branches/{id}/settings` (auto-
// checkout) are the branch-mutating endpoints this page uses.
export default function Branches() {
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [settingsBranch, setSettingsBranch] = useState(null);

  const branchesQ = useQuery({ queryKey: ["branches-list"], queryFn: async () => (await api.get("/branches")).data.branches });

  return (
    <div className="space-y-5 max-w-3xl" data-testid={BRANCHES.page}>
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Branches</h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>Every location under this organization.</p>
        </div>
        <Button data-testid={BRANCHES.addButton} onClick={() => setAddOpen(true)} className="text-white gap-2" style={{ background: "var(--scarlet-accent)" }}>
          <Building2 size={15} /> Add branch
        </Button>
      </motion.div>

      {branchesQ.isError && <ErrorState error={branchesQ.error} onRetry={() => branchesQ.refetch()} />}
      {branchesQ.isLoading && <GymLoader label="Loading branches…" />}
      {branchesQ.data && branchesQ.data.length === 0 && (
        <div className="solid-surface rounded-[14px] p-10 text-center text-sm" data-testid={BRANCHES.emptyState} style={{ color: "var(--scarlet-text-secondary)" }}>No branches yet.</div>
      )}
      {branchesQ.data && branchesQ.data.length > 0 && (
        <div className="solid-surface rounded-[14px] overflow-hidden">
          <table className="w-full text-sm" data-testid={BRANCHES.table}>
            <thead>
              <tr className="text-left" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                <th className="eyebrow px-5 py-3">Branch</th>
                <th className="eyebrow px-5 py-3">Status</th>
                <th className="eyebrow px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {branchesQ.data.map((b) => (
                <tr key={b.id} data-testid={BRANCHES.row(b.id)} style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                  <td className="px-5 py-3.5 font-medium" style={{ color: "var(--scarlet-text)" }}>{b.name}</td>
                  <td className="px-5 py-3.5"><StatusPill status={b.status === "active" ? "active" : "inactive"} label={b.status === "active" ? "Active" : "Inactive"} /></td>
                  <td className="px-5 py-3.5 text-right">
                    <Button size="sm" variant="outline" data-testid={BRANCHES.editSettingsButton(b.id)} onClick={() => setSettingsBranch(b)} className="gap-1.5">
                      <Settings2 size={13} /> Edit branch
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddBranchDialog open={addOpen} onOpenChange={setAddOpen} onCreated={() => queryClient.invalidateQueries({ queryKey: ["branches-list"] })} />
      <BranchSettingsDialog branch={settingsBranch} onOpenChange={(v) => !v && setSettingsBranch(null)} />
    </div>
  );
}

function AddBranchDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setName(""); setError(""); } }, [open]);

  const mutation = useMutation({
    mutationFn: () => api.post("/branches", { name }),
    onSuccess: () => { onCreated(); onOpenChange(false); },
    onError: (e) => setError(formatApiError(e).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal sm:rounded-[14px]" data-testid={BRANCHES.formDialog}>
        <DialogHeader>
          <DialogTitle>Add branch</DialogTitle>
          <DialogDescription>Staff can be assigned to it right away from the Staff screen.</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm shake-on-error" style={{ color: "var(--scarlet-neg)" }} data-testid={BRANCHES.formError}>{error}</p>}
        <FormField label="Branch name" required testId={BRANCHES.nameInput} value={name} onChange={setName} placeholder="e.g. Andheri West" />
        <DialogFooter>
          <SubmitButton
            testId={BRANCHES.formSubmit} disabled={!isFieldValid("text", true, name)} pending={mutation.isPending}
            onClick={() => { setError(""); mutation.mutate(); }} label="Add branch"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BranchSettingsDialog({ branch, onOpenChange }) {
  const queryClient = useQueryClient();
  const [minutes, setMinutes] = useState("50");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [gstin, setGstin] = useState("");
  const [status, setStatus] = useState("active");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [mode, setMode] = useState("rotating");
  const [requirePresence, setRequirePresence] = useState(false);
  const [geofenceEnabled, setGeofenceEnabled] = useState(false);
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState("150");

  const settingsQ = useQuery({
    queryKey: ["branch-settings", branch?.id],
    queryFn: async () => (await api.get(`/branches/${branch.id}/settings`)).data,
    enabled: !!branch,
  });

  const checkinConfigQ = useQuery({
    queryKey: ["branch-checkin-config", branch?.id],
    queryFn: async () => (await api.get(`/branches/${branch.id}/checkin-config`)).data,
    enabled: !!branch,
  });

  useEffect(() => {
    const c = checkinConfigQ.data;
    if (!c) return;
    setMode(c.mode || "rotating");
    setRequirePresence(!!c.require_presence);
    setGeofenceEnabled(!!c.geofence_enabled);
    setLat(c.geofence?.lat != null ? String(c.geofence.lat) : "");
    setLng(c.geofence?.lng != null ? String(c.geofence.lng) : "");
    setRadius(String(c.geofence?.radius_m ?? 150));
  }, [checkinConfigQ.data]);

  useEffect(() => {
    if (settingsQ.data) {
      setMinutes(String(settingsQ.data.auto_checkout_minutes));
      setName(settingsQ.data.name || branch?.name || "");
      setAddress(settingsQ.data.address?.line1 || "");
      setPhone(settingsQ.data.contact?.phone || "");
      setGstin(settingsQ.data.gstin || "");
      setStatus(settingsQ.data.status || "active");
      setNotes(settingsQ.data.notes || "");
    }
  }, [settingsQ.data, branch]);
  useEffect(() => { if (!branch) setError(""); }, [branch]);

  // DEFECT-D-08: renaming a branch or editing its address/contact/GSTIN/active-inactive status —
  // genuinely new, no fallback to the old auto-checkout-only PATCH for these fields.
  const profileMutation = useMutation({
    mutationFn: () => api.patch(`/branches/${branch.id}/profile`, { name, address: { line1: address }, contact: { phone }, gstin: gstin || null, status, notes: notes || null }),
    onError: (e) => setError(formatApiError(e).message),
  });
  const settingsMutation = useMutation({
    mutationFn: () => api.patch(`/branches/${branch.id}/settings`, { auto_checkout_minutes: Number(minutes) }),
    onError: (e) => setError(formatApiError(e).message),
  });
  const checkinConfigMutation = useMutation({
    mutationFn: () => {
      const payload = { mode, require_presence: requirePresence, geofence_enabled: geofenceEnabled };
      // Only send coordinates when they're actually filled in — the backend refuses to enable
      // the geofence without them, and says so.
      if (lat !== "" && lng !== "") {
        payload.geofence = { lat: Number(lat), lng: Number(lng), radius_m: Number(radius) || 150 };
      }
      return api.patch(`/branches/${branch.id}/checkin-config`, payload);
    },
    onError: (e) => setError(formatApiError(e).message),
  });

  const saving = profileMutation.isPending || settingsMutation.isPending || checkinConfigMutation.isPending;
  const save = async () => {
    setError("");
    try {
      await Promise.all([profileMutation.mutateAsync(), settingsMutation.mutateAsync(), checkinConfigMutation.mutateAsync()]);
      queryClient.invalidateQueries({ queryKey: ["branch-settings", branch.id] });
      queryClient.invalidateQueries({ queryKey: ["branch-checkin-config", branch.id] });
      queryClient.invalidateQueries({ queryKey: ["branches-list"] });
      onOpenChange(false);
    } catch {
      // individual mutation onError already surfaced the message
    }
  };

  return (
    <Dialog open={!!branch} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal sm:rounded-[14px]" data-testid={BRANCHES.settingsDialog}>
        <DialogHeader>
          <DialogTitle>Edit branch</DialogTitle>
          <DialogDescription>Name, address, contact and auto-checkout for this branch.</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm shake-on-error" style={{ color: "var(--scarlet-neg)" }}>{error}</p>}
        {settingsQ.isLoading ? <GymLoader label="Loading…" /> : (
          <div className="space-y-3">
            <FormField label="Branch name" required testId={BRANCHES.nameEditInput} value={name} onChange={setName} />
            <FormField label="Address" testId={BRANCHES.addressInput} value={address} onChange={setAddress} placeholder="Street, area, city" />
            <FormField label="Contact phone" testId={BRANCHES.contactPhoneInput} value={phone} onChange={setPhone} placeholder="10-digit phone" />
            <FormField label="GSTIN" testId={BRANCHES.gstinInput} value={gstin} onChange={setGstin} placeholder="e.g. 27ABCDE1234F1Z5" />
            <div className="space-y-1.5">
              <label className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>Status</label>
              <select
                data-testid={BRANCHES.statusSelect} value={status} onChange={(e) => setStatus(e.target.value)}
                className="w-full h-10 rounded-[8px] px-3 text-sm"
                style={{ border: "1px solid var(--scarlet-hairline)", background: "var(--scarlet-surface)", color: "var(--scarlet-text)" }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <FormField label="Auto-checkout minutes" required type="number" testId={BRANCHES.autoCheckoutInput} value={minutes} onChange={setMinutes} helperText="Members still checked in past this many minutes are auto-checked-out. 1 to 1440." />
            <div className="space-y-1.5">
              <label className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>Notes <span className="text-xs font-normal" style={{ color: "var(--scarlet-text-secondary)" }}>(optional, internal only)</span></label>
              <Textarea data-testid={BRANCHES.notesInput} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Anything worth remembering about this branch…" />
            </div>

            <div className="pt-3 space-y-3" style={{ borderTop: "1px solid var(--scarlet-hairline)" }}>
              <p className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>Member self check-in</p>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>Check-in code</label>
                <select
                  data-testid={BRANCHES.checkinModeSelect} value={mode} onChange={(e) => setMode(e.target.value)}
                  className="w-full h-10 rounded-[8px] px-3 text-sm"
                  style={{ border: "1px solid var(--scarlet-hairline)", background: "var(--scarlet-surface)", color: "var(--scarlet-text)" }}
                >
                  <option value="rotating">Rotating — changes every 60s, show on a screen</option>
                  <option value="static">Fixed — safe to print on a poster</option>
                </select>
                <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
                  {mode === "rotating"
                    ? "A photographed code stops working within a couple of minutes."
                    : "Simplest to run, but anyone who photographs the poster could check in from home."}
                </p>
              </div>

              <label className="flex items-start gap-2 text-sm" style={{ color: "var(--scarlet-text)" }}>
                <input
                  type="checkbox" data-testid={BRANCHES.requirePresenceCheckbox} checked={requirePresence}
                  onChange={(e) => setRequirePresence(e.target.checked)} className="mt-0.5"
                />
                <span>
                  Require proof of presence
                  <span className="block text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
                    Members must scan the QR or type the code — a plain "I'm here" tap is refused.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm" style={{ color: "var(--scarlet-text)" }}>
                <input
                  type="checkbox" data-testid={BRANCHES.geofenceCheckbox} checked={geofenceEnabled}
                  onChange={(e) => setGeofenceEnabled(e.target.checked)} className="mt-0.5"
                />
                <span>
                  Also check they're physically nearby
                  <span className="block text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
                    Needs the coordinates below, and the member's location permission.
                  </span>
                </span>
              </label>

              {geofenceEnabled && (
                <div className="grid grid-cols-3 gap-2">
                  <FormField label="Latitude" testId={BRANCHES.geofenceLatInput} value={lat} onChange={setLat} placeholder="19.1197" />
                  <FormField label="Longitude" testId={BRANCHES.geofenceLngInput} value={lng} onChange={setLng} placeholder="72.8464" />
                  <FormField label="Radius (m)" type="number" testId={BRANCHES.geofenceRadiusInput} value={radius} onChange={setRadius} />
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <SubmitButton
            testId={BRANCHES.settingsSubmit} disabled={!name.trim() || !minutes || Number(minutes) < 1 || Number(minutes) > 1440} pending={saving}
            onClick={save} label="Save"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
